import { DropdownSelect, FormField, FormHelp, Icon, Label, Select } from '@zillow/constellation-10';

import { getConstellationIcon } from '../../../utils/iconUtils';

import type {
  FeeCategoryDictionaryItemDto,
  FeeDictionaryItemDisplayDto,
  FeePaymentFrequencyDictionaryItemDto,
} from '@zg-rentals/ts-rental-manager-api-client';

type Option = {
  label: string;
  value: string;
  icon?: JSX.Element;
};

type GroupedOption = {
  heading: {
    label: string;
  };
  options: Array<Option>;
};

type Props = {
  value: string;
  onChange: (value: string) => void;
  dictionaryItems: Record<string, FeeDictionaryItemDisplayDto> | null;
  disabled?: boolean;
  fieldLabel: string;
  formHelpText?: string;
  hasIcon?: boolean;
  required?: boolean;
  onBlur: (fieldName: string, value: string | number) => void;
  errorMessage?: string;
  fieldName: string;
  readOnly?: boolean;
  feePaymentFrequencyCategory?: Record<string, FeePaymentFrequencyDictionaryItemDto> | null;
};

const FeeDropdownSelectField = ({
  value = '',
  onChange,
  dictionaryItems,
  disabled = false,
  fieldLabel,
  formHelpText = '',
  hasIcon = false,
  required = false,
  onBlur = () => {},
  errorMessage = '',
  fieldName,
  readOnly = false,
  feePaymentFrequencyCategory,
}: Props) => {
  const isPaymentFrequencyField = fieldName === 'feePaymentFrequencyType';
  const shouldUseGroupedOptions = isPaymentFrequencyField && feePaymentFrequencyCategory;

  let dropdownOptions: Array<Option | GroupedOption> = [];

  if (shouldUseGroupedOptions && feePaymentFrequencyCategory) {
    // Create grouped options for payment frequency
    dropdownOptions = Object.entries(feePaymentFrequencyCategory).map(([_categoryKey, categoryData]) => ({
      heading: {
        label: categoryData.displayName,
      },
      options: Object.entries(categoryData.feePaymentFrequencyType).map(([_frequencyKey, frequencyData]) => ({
        value: _frequencyKey,
        label: frequencyData.displayName,
      })),
    }));
  } else if (dictionaryItems) {
    // Create flat options for other fields
    Object.keys(dictionaryItems).forEach((key) => {
      if (hasIcon) {
        const { displayName, feeCategoryIconName } = dictionaryItems[key] as FeeCategoryDictionaryItemDto;
        const IconComponent = getConstellationIcon({ iconName: feeCategoryIconName });
        dropdownOptions.push({
          value: key,
          label: displayName,
          icon: IconComponent ? <Icon render={<IconComponent />} /> : undefined,
        });
      } else {
        const { displayName } = dictionaryItems[key];
        dropdownOptions.push({
          value: key,
          label: displayName,
        });
      }
    });
  }

  const onOptionSelect = (option: string | { value: string; label: string }) => {
    const selectedValue = typeof option === 'string' ? option : option.value;
    onChange(selectedValue);
    // validate new value on selection to clear out the empty state error right away for better UX
    onBlur(fieldName, selectedValue);
  };

  // Helper function to flatten grouped options for read-only mode
  const flattenOptions = (options: Array<Option | GroupedOption>): Array<Option> => {
    return options.flatMap((option) => {
      if ('heading' in option) {
        return option.options;
      }
      return option;
    });
  };

  const renderFlattenedOptions = () => {
    const flatOptions = flattenOptions(dropdownOptions);
    return flatOptions.map((option) => (
      <option key={option.value} value={option.value}>
        {option.label}
      </option>
    ));
  };

  return (
    <FormField
      data-testid={`CostsAndFeesFields-${fieldName}Dropdown`}
      required={required}
      error={errorMessage !== ''}
      description={
        readOnly && formHelpText === '' ? null : <FormHelp>{errorMessage ? errorMessage : formHelpText}</FormHelp>
      }
      label={<Label>{fieldLabel}</Label>}
      onBlur={
        readOnly
          ? () => {}
          : (e: React.FocusEvent<HTMLInputElement>) => {
              onBlur(fieldName, e.target.value);
            }
      }
    >
      {readOnly ? (
        <Select required={required} readOnly={true} defaultValue={value}>
          {renderFlattenedOptions()}
        </Select>
      ) : (
        <DropdownSelect
          value={value}
          disabled={disabled}
          placeholder="Select"
          options={dropdownOptions}
          showLabelForValue={true}
          onOptionSelect={onOptionSelect}
        />
      )}
    </FormField>
  );
};

export default FeeDropdownSelectField;
